import { Box, IconButton, Button, Textarea, Text, SimpleGrid, Separator } from "@chakra-ui/react"
import { LuThumbsUp, LuThumbsDown } from "react-icons/lu";
import { useAccount } from "./account-context";
import { useChatbotContext } from "./chatbot-context";
import { sendFeedback } from "@/api";
import { useState } from "react";
import { MoreOptions } from "./more-options";
import { 
    DialogActionTrigger, 
    DialogBody, 
    DialogCloseTrigger, 
    DialogContent, 
    DialogFooter, 
    DialogHeader, 
    DialogRoot, 
    DialogTitle 
} from "./dialog";
import { Field } from "./field";
import { Checkbox } from "./checkbox";
import { Fieldset } from "./fieldset";
import Markdown from "react-markdown";
import rehypeRaw from 'rehype-raw'
import { useColorPalette } from "./color-palette-context";

interface FeedbackProps {
    fontFamily?: string;
    messageIndex: number;
    isLast: boolean;
    userQuery?: string;
    botAnswer?: string;
}

export const Feedback = (props: FeedbackProps) => {
    const { fontFamily, messageIndex, isLast, userQuery, botAnswer } = props;
    const { currentSessionId, allowedBots } = useAccount();
    const { selectedChatbotValue } = useChatbotContext();
    const [submitted, setSubmitted] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [feedbackType, setFeedbackType] = useState<'thumbs_up' | 'thumbs_down' | null>(null);
    const [comment, setComment] = useState("");
    const [idealAnswer, setIdealAnswer] = useState("");
    const {colorPalette} = useColorPalette();

    const markdownComponents = {
        p: (props: any) => <Text {...props} />,
        ul: (props: any) => <Box as="ul" ml="6" mb="2" style={{ listStyleType: 'disc' }} {...props} />,
        ol: (props: any) => <Box as="ol" ml="6" mb="2" style={{ listStyleType: 'decimal' }} {...props} />,
        li: (props: any) => <Box as="li" mb="1" {...props} />,
        a: (props: any) => <Box as="a" color={`${colorPalette}.solid`} textDecoration="underline" {...props} />,
        h1: (props: any) => <Text fontSize="2xl" fontWeight="bold" mb="2" {...props} />,
        h2: (props: any) => <Text fontSize="xl" fontWeight="bold" mb="2" {...props} />,
        h3: (props: any) => <Text fontSize="lg" fontWeight="bold" mb="2" {...props} />,
        code: (props: any) => <Box as="code" bg="var(--chakra-colors-bg-muted)" p="1" borderRadius="sm" {...props} />,
        pre: (props: any) => <Box as="pre" bg="var(--chakra-colors-bg-muted)" p="2" borderRadius="md" overflowX="auto" mb="2" {...props} />,
    };

    // Specific reasons for negative feedback
    const [reasons, setReasons] = useState({
        notFactuallyCorrect: false,
        notHelpful: false,
        notFollowingInstructions: false,
        harmfulOrOffensive: false,
        answerTookTooLong: false,
        irrelevantInfo: false,
    });

    if (!isLast || submitted) {
        return null;
    }

    const agentId = allowedBots?.[selectedChatbotValue]?.agentId;

    const handleFeedbackSubmit = async () => {
        if (currentSessionId && agentId && feedbackType) {
            const selectedReasons = Object.entries(reasons)
                .filter(([_, value]) => value)
                .map(([key, _]) => {
                    switch(key) {
                        case 'notFactuallyCorrect': return "Not factually correct";
                        case 'notHelpful': return "Not helpful";
                        case 'notFollowingInstructions': return "Not following instructions";
                        case 'harmfulOrOffensive': return "Harmful or offensive";
                        case 'answerTookTooLong': return "Answer took too long";
                        case 'irrelevantInfo': return "Answer contained irrelevant info";
                        default: return key;
                    }
                });

            try {
                await sendFeedback(
                    currentSessionId, 
                    agentId, 
                    messageIndex, 
                    feedbackType, 
                    feedbackType === 'thumbs_down' ? idealAnswer : null, 
                    comment,
                    feedbackType === 'thumbs_down' ? selectedReasons : null
                );
                setSubmitted(true);
                setIsOpen(false);
            } catch (error) {
                console.error("Failed to send feedback:", error);
            }
        }
    }

    const handleThumbsUp = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setFeedbackType('thumbs_up');
        setIsOpen(true);
    }

    const handleThumbsDown = (e : React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setFeedbackType('thumbs_down');
        setIsOpen(true);
    }

    const setReason = (reason: keyof typeof reasons, value: boolean) => {
        setReasons(prev => ({ ...prev, [reason]: value }));
    }

    return(
        <Box display={'flex'} flexDir={'row'} mt={1}>
            <IconButton
                borderRadius={'full'}
                size={'xs'}
                variant={'ghost'}
                onClick={handleThumbsUp}
                aria-label="Thumbs up"
            >
                <LuThumbsUp aria-hidden="true" />
            </IconButton>
            <IconButton
                borderRadius={'full'}
                size={'xs'}
                variant={'ghost'}
                onClick={handleThumbsDown}
                aria-label="Thumbs down"
            >
                <LuThumbsDown aria-hidden="true" />
            </IconButton>
            <MoreOptions botAnswer={botAnswer} />

            <DialogRoot open={isOpen} onOpenChange={(e) => setIsOpen(e.open)} size="lg" placement="center" motionPreset="slide-in-bottom" children={
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle fontFamily={fontFamily} fontWeight="400">
                            {feedbackType === 'thumbs_up' ? 'Provide Feedback' : 'Report an Issue'}
                        </DialogTitle>
                    </DialogHeader>
                    <DialogBody pb="4">
                        <Box mb="4" p="3" borderRadius="md" bg="var(--chakra-colors-bg-muted)" fontSize="xs" fontFamily={fontFamily}>
                            <Text fontWeight="bold" mb="1">Your Question:</Text>
                            <Text mb="3" color="var(--chakra-colors-fg-muted)">{userQuery || "N/A"}</Text>
                            <Separator mb="3" />
                            <Text fontWeight="bold" mb="1">Bot Answer:</Text>
                            <Text color="var(--chakra-colors-fg-muted)"><Markdown rehypePlugins={[rehypeRaw]} components={markdownComponents}>{botAnswer || "N/A"}</Markdown></Text>
                        </Box>

                        <Field label="Additional Comments" fontFamily={fontFamily} mb="4">
                            <Textarea 
                                placeholder="Type your feedback here..." 
                                value={comment} 
                                onChange={(e) => setComment(e.target.value)}
                                fontFamily={fontFamily}
                                resize="none"
                                size="sm"
                            />
                        </Field>

                        {feedbackType === 'thumbs_down' && (
                            <>
                                <Fieldset legend="What went wrong?" fontFamily={fontFamily} mb="2">
                                    <SimpleGrid columns={2} gap="2">
                                        <Checkbox 
                                            id={`not-factually-correct-${messageIndex}`}
                                            size="sm"
                                            checked={reasons.notFactuallyCorrect} 
                                            onCheckedChange={(e) => setReason('notFactuallyCorrect', !!e.checked)}
                                            fontFamily={fontFamily}
                                        >
                                            Not factually correct
                                        </Checkbox>
                                        <Checkbox 
                                            id={`not-helpful-${messageIndex}`}
                                            size="sm"
                                            checked={reasons.notHelpful} 
                                            onCheckedChange={(e) => setReason('notHelpful', !!e.checked)}
                                            fontFamily={fontFamily}
                                        >
                                            Not helpful
                                        </Checkbox>
                                        <Checkbox 
                                            id={`not-following-instructions-${messageIndex}`}
                                            size="sm"
                                            checked={reasons.notFollowingInstructions} 
                                            onCheckedChange={(e) => setReason('notFollowingInstructions', !!e.checked)}
                                            fontFamily={fontFamily}
                                        >
                                            Not following instructions
                                        </Checkbox>
                                        <Checkbox 
                                            id={`harmful-or-offensive-${messageIndex}`}
                                            size="sm"
                                            checked={reasons.harmfulOrOffensive} 
                                            onCheckedChange={(e) => setReason('harmfulOrOffensive', !!e.checked)}
                                            fontFamily={fontFamily}
                                        >
                                            Harmful or offensive
                                        </Checkbox>
                                        <Checkbox 
                                            id={`answer-took-too-long-${messageIndex}`}
                                            size="sm"
                                            checked={reasons.answerTookTooLong} 
                                            onCheckedChange={(e) => setReason('answerTookTooLong', !!e.checked)}
                                            fontFamily={fontFamily}
                                        >
                                            Answer took too long
                                        </Checkbox>
                                        <Checkbox 
                                            id={`irrelevant-info-${messageIndex}`}
                                            size="sm"
                                            checked={reasons.irrelevantInfo} 
                                            onCheckedChange={(e) => setReason('irrelevantInfo', !!e.checked)}
                                            fontFamily={fontFamily}
                                        >
                                            Answer contained irrelevant info
                                        </Checkbox>
                                    </SimpleGrid>
                                </Fieldset>

                                <Field label="Ideal Answer" fontFamily={fontFamily}>
                                    <Textarea 
                                        placeholder="Type the ideal answer here..." 
                                        value={idealAnswer} 
                                        onChange={(e) => setIdealAnswer(e.target.value)}
                                        fontFamily={fontFamily}
                                        resize="none"
                                        size="sm"
                                    />
                                </Field>
                            </>
                         )}
                    </DialogBody>
                    <DialogFooter>
                        <DialogActionTrigger asChild>
                            <Button variant="outline" fontFamily={fontFamily} size="sm">Cancel</Button>
                        </DialogActionTrigger>
                        <Button onClick={handleFeedbackSubmit} fontFamily={fontFamily} size="sm">Confirm</Button>
                    </DialogFooter>
                    <DialogCloseTrigger />
                </DialogContent>
            }/>
        </Box>
    )
}
